import React, {useState} from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda/src/index';
import Modal from 'react-bootstrap/lib/Modal';
import PreferredLocale from 'components/authentication/PreferredLocale';
import PreferredCurrency from 'components/authentication/PreferredCurrency';
import Button from 'helpers/Button';
import {createLanguageUrl, createResultsUrl} from 'components/authentication/helper';
import {fetchCurrencyDetails} from 'providers/configuration';
import WarningIcon from 'components/navbar/icons/WarningIcon';

const createLanguages = (locales) => R.zipWith((x, y) => {
  return {
    locale: x,
    label: R.prop('language', y),
    language: R.prop('lang', y),
    url: R.prop('url', y),
    order: R.prop('order', y)
  };
}, R.keys(locales), R.values(locales));

const RegionalSettingModal = ({
  locales,
  preferredLocale,
  brandLocale,
  isPreferredCurrencyEnabled,
  currencies,
  preferredCurrency,
  toggleModal,
  translations,
  isSeatingOrPaymentPage
}) => {
  const [locale, setLocale] = useState(preferredLocale);
  const [currency, setCurrency] = useState(preferredCurrency);
  const [currencyChanged, setCurrencyChanged] = useState(false);

  const languages = createLanguages(locales);
  languages.sort((a, b) => a.order - b.order);

  const {language: preferredLanguage, lang: preferredLang} = locales[locale];
  const {regionalSettings, save} = translations;

  const onChangeLocale = (locale) => {
    setLocale(locale);
  };

  const onChangeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    setCurrencyChanged(newCurrency !== preferredCurrency);
  };

  const onSave = async () => {
    const {body: {results: {data: {currencyDetails}}}} = await fetchCurrencyDetails(currency);
    sessionStorage.setItem('currency-storage', JSON.stringify({...currencyDetails}));

    const selLanguage = languages.find((language) => language.locale === locale);

    if (isSeatingOrPaymentPage && currencyChanged) {
      const searchUrl = sessionStorage.getItem('searchUrl');
      toggleModal();
      window.location.href = createResultsUrl({selLanguage, searchUrl});
      return;
    }

    const href = createLanguageUrl({selLanguage, preferredLang: locales[preferredLocale].lang});
    if (location.href === href) {
      location.reload();
    } else {
      location.href = href;
    }
  };

  const currencyWarning = isSeatingOrPaymentPage ? (
    <div className="regional-settings-warning">
      <WarningIcon className="regional-settings-warning__icon" />
      <span>{translations.seating_page_currency_info}</span>
    </div>
  ) : null;

  return (
    <Modal show onHide={toggleModal} dialogClassName="regional-setting-modal">
      <div className="bg-snow p-3 border-radius-lg">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="h4 d-flex align-items-center font-bold">
            {regionalSettings}
          </div>
          <button type="button" className="close text-primary" onClick={toggleModal}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <form>
          <PreferredLocale
            languages={languages}
            locales={locales}
            brandLocale={brandLocale}
            locale={locale}
            preferredLang={preferredLang}
            preferredLanguage={preferredLanguage}
            onChangeLocale={onChangeLocale}
            translations={translations}
          />
          {isPreferredCurrencyEnabled && currencies.length > 0 && (
            <PreferredCurrency
              currencies={currencies}
              currency={currency}
              preferredCurrency={preferredCurrency}
              onChangeCurrency={onChangeCurrency}
              translations={translations}
              warning={currencyWarning}
            />
          )}
        </form>
        <Button
          aria-label={save}
          status="primary"
          className="w-1-1"
          onClick={onSave}
        >
          {save}
        </Button>
      </div>
    </Modal>
  );
};

RegionalSettingModal.propTypes = {
  locales: PropTypes.object.isRequired,
  currencies: PropTypes.array.isRequired,
  brandLocale: PropTypes.string.isRequired,
  preferredLocale: PropTypes.string.isRequired,
  isPreferredCurrencyEnabled: PropTypes.bool.isRequired,
  preferredCurrency: PropTypes.string.isRequired,
  toggleModal: PropTypes.func.isRequired,
  translations: PropTypes.shape({
    regionalSettings: PropTypes.string.isRequired,
    save: PropTypes.string.isRequired
  }),
  isSeatingOrPaymentPage: PropTypes.bool
};

RegionalSettingModal.defaultProps = {
  isSeatingOrPaymentPage: false
};

export default RegionalSettingModal;
